/**
 * PublicProfilePreview (web): iframe embed of the canonical profile surface
 * served by `apps/web`.
 *
 * Why an iframe?
 *  - `apps/web` is the single source of truth for what a visitor sees on the
 *    public URL. Embedding it here guarantees the editor preview never drifts
 *    from production.
 *  - Layout/typography lives in Tailwind classes on the web side. No need to
 *    duplicate the renderer in React Native Web.
 *
 * Bridge protocol: see `@tabbio/types/public-profile-preview-bridge`:
 *  - Mobile → iframe: `INIT` (once), then debounced `UPDATE`s
 *  - Iframe → mobile: `READY`, `NAVIGATE` (link clicks), `RESIZE` (optional)
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  PROFILE_PREVIEW_MESSAGE_TYPES,
  isProfilePreviewMessage,
  type ProfilePreviewOutgoingMessage,
  type PublicProfileRenderOpenRole,
} from '@tabbio/types';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { buildPublicProfilePreviewUrl } from '../../lib/public-web';
import { useLocale } from '../../hooks/useLocale';
import { useTheme } from '../../hooks/useTheme';
import {
  buildPublicProfilePreviewPage,
  type PublicProfilePreviewModel,
} from './PublicProfilePreviewModel';

type PublicProfilePreviewProps = {
  model: PublicProfilePreviewModel;
  /** Raw editor inputs the live-preview iframe needs to build the page payload. */
  modelInput: Parameters<typeof buildPublicProfilePreviewPage>[0];
  isPubliclyVisible: boolean;
  onEdit: () => void;
  onShare?: () => void;
  onRoleMore?: (role: PublicProfileRenderOpenRole) => void;
  onOpenSettings: () => void;
  onOpenHref: (href: string) => void;
  onOpenMenu?: () => void;
  onUpgrade?: () => void;
  onOpenTalentPool?: () => void;
};

const PREVIEW_OVERLAY_DENSITY = {
  editInsetInlineEnd: 22,
  editBottom: 20,
  editButtonSize: 52,
  editShadow: '0 12px 28px rgba(0,0,0,0.18)',
  editIconSize: 20,
} as const;

const UPDATE_DEBOUNCE_MS = 80;

/**
 * How long to wait after the frame's `load` event for its `READY` handshake.
 *
 * An iframe fires no `onerror` for an HTTP error - a 500 from apps/web loads
 * as a perfectly good error document, so the only signal that the preview is
 * broken is `READY` never arriving. Without this the editor sat on its
 * loading spinner forever with nothing to act on.
 */
const READY_TIMEOUT_MS = 6000;

/**
 * Marks the frame as failed. The message itself is translated at render time —
 * this only records THAT it failed, since the timer fires outside the render.
 */
const PREVIEW_LOAD_FAILED = 'preview-load-failed';

function decodeTrackedHref(href: string) {
  try {
    const url = new URL(
      href,
      typeof window !== 'undefined'
        ? window.location.href
        : 'https://tabbio.com',
    );
    const target = url.searchParams.get('target');
    if (target) return target;
    return href;
  } catch {
    return href;
  }
}

export function PublicProfilePreview({
  model,
  modelInput,
  isPubliclyVisible,
  onEdit,
  onOpenSettings,
  onOpenHref,
}: PublicProfilePreviewProps) {
  const { t } = useTranslation('common');
  const { locale } = useLocale();
  const { isDark } = useTheme();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);
  // Mirrored into a ref so the `load` timer can read the current value without
  // being re-created (and restarted) on every render.
  const isReadyRef = useRef(false);
  isReadyRef.current = isReady;
  const readyTimerRef = useRef<number | null>(null);

  const clearReadyTimer = useCallback(() => {
    if (readyTimerRef.current !== null) {
      window.clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearReadyTimer, [clearReadyTimer]);

  const handle =
    model.document.canonicalHandle || model.document.owner.username || 'tabbio';
  /**
   * The preview renders from apps/web on a DIFFERENT origin, so the app's
   * NEXT_LOCALE cookie is not shared and locale negotiation falls through to
   * Accept-Language — an Arabic employer previewed their page in English.
   * `?lang=` is the first source apps/web's public locale negotiation honours.
   *
   * The URL deliberately stays on the INTERNAL `/public-profile/{handle}/
   * preview` path: only that route sends the framing-permissive CSP
   * (`frame-ancestors … http://localhost:8081 https://app.tabbio.com`). The
   * canonical `/{handle}/preview` answers `X-Frame-Options: SAMEORIGIN`, so
   * framing it here would blank the preview.
   *
   * `?theme=` carries the app's palette across the origin boundary the same
   * way `?lang=` does: without it the iframe always rendered the light
   * published design and sat as a white slab inside a dark editor. Changing
   * the app theme changes the src and reloads the frame; the draft is
   * re-sent as the INIT snapshot on the next READY, so nothing is lost.
   */
  const previewUrl = useMemo(() => {
    const base = buildPublicProfilePreviewUrl(handle);
    if (!base) return null;
    try {
      const url = new URL(base);
      url.searchParams.set('lang', locale);
      url.searchParams.set('theme', isDark ? 'dark' : 'light');
      return url.toString();
    } catch {
      return base;
    }
  }, [handle, isDark, locale]);

  const payload = useMemo(
    () => ({
      page: buildPublicProfilePreviewPage(modelInput),
      isPubliclyVisible,
    }),
    [modelInput, isPubliclyVisible],
  );

  // Latest payload kept in a ref so the debounced sender always uses fresh data
  // without re-creating the timer on every keystroke.
  const latestPayloadRef = useRef(payload);
  latestPayloadRef.current = payload;

  const postToIframe = useCallback(
    (
      message:
        | ProfilePreviewOutgoingMessage
        | { type: string; payload?: unknown },
    ) => {
      const target = iframeRef.current?.contentWindow;
      if (!target) return;
      try {
        target.postMessage(message, '*');
      } catch {
        /* swallow */
      }
    },
    [],
  );

  // Listen for iframe → mobile messages.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data;
      if (!isProfilePreviewMessage(data)) return;
      if (data.type === PROFILE_PREVIEW_MESSAGE_TYPES.Ready) {
        clearReadyTimer();
        setIframeError(null);
        setIsReady(true);
        // Send the initial snapshot now that the page is ready to receive it.
        postToIframe({
          type: PROFILE_PREVIEW_MESSAGE_TYPES.Init,
          payload: latestPayloadRef.current,
        });
        return;
      }
      if (data.type === PROFILE_PREVIEW_MESSAGE_TYPES.Navigate) {
        const href = decodeTrackedHref(data.payload.href);
        if (href) onOpenHref(href);
        return;
      }
    }
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [clearReadyTimer, onOpenHref, postToIframe]);

  // A fresh src (theme or locale change) re-runs the handshake from scratch.
  useEffect(() => {
    clearReadyTimer();
    setIsReady(false);
    setIframeError(null);
  }, [clearReadyTimer, previewUrl]);

  const handleIframeLoad = useCallback(() => {
    clearReadyTimer();
    if (isReadyRef.current) return;
    readyTimerRef.current = window.setTimeout(() => {
      readyTimerRef.current = null;
      if (!isReadyRef.current) setIframeError(PREVIEW_LOAD_FAILED);
    }, READY_TIMEOUT_MS);
  }, [clearReadyTimer]);

  // Push debounced UPDATEs whenever the draft changes (after Ready).
  useEffect(() => {
    if (!isReady) return;
    const handle = window.setTimeout(() => {
      postToIframe({
        type: PROFILE_PREVIEW_MESSAGE_TYPES.Update,
        payload,
      });
    }, UPDATE_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [isReady, payload, postToIframe]);

  // Fallback when the canonical handle/site URL aren't configured.
  if (!previewUrl) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          padding: 40,
          color: 'var(--text-tertiary)',
          fontSize: 14,
        }}
      >
        Live preview unavailable: set EXPO_PUBLIC_SITE_URL.
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
        background: 'var(--bg)',
      }}
    >
      {!isPubliclyVisible ? (
        <button
          type="button"
          onClick={onOpenSettings}
          style={{
            position: 'absolute',
            top: 12,
            insetInlineStart: '50%',
            transform: 'translateX(-50%)',
            zIndex: 60,
            maxWidth: 'min(420px, calc(100vw - 32px))',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderRadius: 9999,
            background: 'var(--menu-bg, #fff)',
            color: 'var(--text-primary, #0d0d0d)',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <MaterialSymbol name="visibility_off" size={16} />
          {t('publicProfile.previewNotPublishedTitle', {
            defaultValue: 'Not published yet',
          })}
          <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>
            ·{' '}
            {t('publicProfile.previewNotPublishedHint', {
              defaultValue: 'Tap to publish',
            })}
          </span>
        </button>
      ) : null}

      {iframeError ? (
        <div
          style={{
            display: 'grid',
            placeItems: 'center',
            padding: 40,
            color: 'var(--text-tertiary)',
            fontSize: 14,
            textAlign: 'center',
            gap: 8,
          }}
        >
          <MaterialSymbol name="cloud_off" size={20} />
          <span>
            {t('publicProfile.previewFailed', {
              defaultValue:
                'Preview failed to load. Check that the public site and the API are running.',
            })}
          </span>
          {/* The frame is cross-origin, so the shell cannot read why it failed
              (a 404, a 500 from an unreachable API, or nothing listening all
              look identical from here). Naming the URL it tried is the one
              concrete thing that makes this diagnosable. */}
          {previewUrl ? (
            <span
              style={{
                fontSize: 12,
                wordBreak: 'break-all',
                opacity: 0.75,
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {previewUrl}
            </span>
          ) : null}
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          title={t('publicProfile.editSheetTitle', { defaultValue: 'Preview' })}
          src={previewUrl}
          onLoad={handleIframeLoad}
          onError={() => setIframeError(PREVIEW_LOAD_FAILED)}
          style={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            height: '100%',
            border: 0,
            background: 'transparent',
            opacity: isReady ? 1 : 0,
            transition: 'opacity 220ms cubic-bezier(0.22,1,0.36,1)',
          }}
        />
      )}

      {!isReady && !iframeError ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--bg)',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              border: '2px solid rgba(0,0,0,0.15)',
              borderTopColor: 'var(--text-primary)',
              borderRadius: '50%',
              animation: 'tabbio-spin 0.8s linear infinite',
              display: 'inline-block',
            }}
          />
          <style>{`@keyframes tabbio-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : null}

      {model.document.ownerKind !== 'company' ? (
        <div
          style={{
            position: 'fixed',
            insetInlineEnd: PREVIEW_OVERLAY_DENSITY.editInsetInlineEnd,
            bottom: PREVIEW_OVERLAY_DENSITY.editBottom,
            zIndex: 65,
          }}
        >
          <button
            type="button"
            onClick={onEdit}
            aria-label={t('publicProfile.editSheetTitle', {
              defaultValue: 'Edit',
            })}
            style={{
              width: PREVIEW_OVERLAY_DENSITY.editButtonSize,
              height: PREVIEW_OVERLAY_DENSITY.editButtonSize,
              borderRadius: 999,
              border: 'none',
              background: 'var(--tabbio-text, #111827)',
              color: 'var(--tabbio-bg, #fff)',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              boxShadow: PREVIEW_OVERLAY_DENSITY.editShadow,
              transition: 'transform 160ms cubic-bezier(0.22,1,0.36,1)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.transform =
                'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.transform =
                'translateY(0)';
            }}
          >
            <MaterialSymbol
              name="edit"
              size={PREVIEW_OVERLAY_DENSITY.editIconSize}
            />
          </button>
        </div>
      ) : null}
    </div>
  );
}
